import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Slide, TextField, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, { forwardRef, useEffect, useState } from 'react'
import { theme } from '../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import { emailTemplate } from '../CandidateManagement/CandidateContent/CandidateContent';
import ReactQuill from 'react-quill';
import { validateEmail } from '../Validation/validation';
import { removeSpacing, sanitizeContent } from '../../Common/Sanitize/sanitizeReactQuill';

const Transition = forwardRef((
    props: TransitionProps & { children: React.ReactElement<any, any>; },
    ref: React.Ref<unknown>
) => {
    return <Slide direction="left" ref={ref} {...props} />;
});

interface NotifyInternalTeamProps {
    open: boolean;
    onClose: () => void;
    templatesDropdown: emailTemplate[];
    recruiterName: string;
    candidatesList: { mapId: number, candidateName: string, jobTitle: string }[];
    sendNotify: (payLoad: any) => void
}

const NotifyInternalTeam: React.FC<NotifyInternalTeamProps> = ({ onClose, open, templatesDropdown, recruiterName, candidatesList, sendNotify }) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [emailSubject, setEmailSubject] = useState('')
    const [emailBody, setEmailBody] = useState('')
    const [selectedTeam, setSelectedTeam] = useState('')
    const [filteredTemplates, setFilteredTemplates] = useState<emailTemplate[]>([])
    const [emailNum, setEmailNum] = useState<number | null>(null)
    const [errors, setErrors] = useState<{ [key: string]: string }>({})


    useEffect(() => {
        if (open) {
            setFilteredTemplates(templatesDropdown.filter((e) => ["Onboarding - IT", "Onboarding - HR", "Onboarding - Admin","Onboarding - Others"].includes(e.EmailKey)))
        }
        setSelectedTeam('')
        setEmailBody('')
        setEmailNum(null)
        setEmailSubject('')
        setEmails([])
    }, [templatesDropdown, open])

    const handleSelectTeamTemplate = (event: SelectChangeEvent<string>) => {
        const selectedTemplate = filteredTemplates.find(
            (e) => e.EmailKey === event.target.value
        );

        if (selectedTemplate) {
            // Build candidate table HTML
            const candidateTable = `
                <ul style="margin:0;">
                    ${candidatesList
                    .map(
                        (c) => `
                        <li style="margin:4px 0;">
                        ${c.candidateName} – ${c.jobTitle}
                        </li>
                    `
                    )
                    .join("")}
                </ul>
                `;



            // Replace placeholders
            let body = selectedTemplate.EmailBody
                .replace("@Recruiter", recruiterName)
                .replace("@CandidateList", candidateTable);

            setEmailNum(selectedTemplate.EmailNum);
            setEmailSubject(selectedTemplate.EmailSubject);
            setEmailBody(body);
            setSelectedTeam(selectedTemplate.EmailKey);

            setErrors({})
        }
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const value = (e.target as HTMLInputElement).value.trim();
            if (value && !emails.includes(value)) {
                setEmails([...emails, value]);
            }
            (e.target as HTMLInputElement).value = "";
            setErrors(prev => ({ ...prev, emails: "" }));
        }
    };

    const handleDeleteEmail = (emailToDelete: string) => {
        setEmails(emails.filter((em) => em !== emailToDelete));
    };

    const handleSend = async () => {
        const newErrors: { [key: string]: string } = {};

        // Validate emails
        if (emails.length === 0) {
            newErrors.emails = "Please add at least one email";
        } else {
            const invalidEmails = emails.filter(
                (em) => !validateEmail(em)
            );
            if (invalidEmails.length > 0) {
                newErrors.emails = `Invalid email(s): ${invalidEmails.join(", ")}`;
            }
        }

        // Validate team
        if (!selectedTeam.trim()) {
            newErrors.team = "Please select a team";
        }

        // Validate subject
        if (!emailSubject.trim()) {
            newErrors.subject = "Subject is required";
        }
        const sanitizedValue = sanitizeContent(emailBody)
        // Validate email body
        if (!sanitizedValue) {
            newErrors.body = "Email body cannot be empty";
        }

        setErrors(newErrors);

        if(Object.keys(newErrors).length > 0){
            return
        }

        const payLoad = {
            EmailsList: emails,
            MapIdList: candidatesList.map((c) => { return c.mapId }),
            EmailBody: removeSpacing(emailBody),
            EmailSubject: emailSubject,
            EmailNum: emailNum,
            EmailKey: selectedTeam
        }

        await sendNotify(payLoad)
    };

    const handleInputChange = (field: string, value: string) => {
        switch (field) {
            case "subject":
                setEmailSubject(value);
                if (errors.subject) {
                    setErrors(prev => ({ ...prev, subject: "" }));
                }
                break;

            case "body":
                setEmailBody(value);
                if (errors.body) {
                    setErrors(prev => ({ ...prev, body: "" }));
                }
                break;

            default:
                break;
        }
    };




    return (
        <Dialog
            open={open}
            onClose={() => {}}
            TransitionComponent={Transition}
            fullScreen
            BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
            PaperProps={{
                sx: {
                    maxWidth: '22vw',
                    height: '100vh',
                    margin: 0,
                    overflow: 'hidden',
                    position: 'absolute',
                    right: 0,
                },
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ display: 'flex', p: '2vh 1.5vw 0px 1vw' }}>
                <Box
                    color={theme.palette.secondary.main}
                    sx={{
                        border: '1px solid #EEEEEE',
                        bgcolor: theme.palette.secondary.light,
                        margin: '0.3vw',
                        mr: '1vw',
                        width: '3vw',
                        height: '3vw',
                        borderRadius: '1vw',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <i className="fa-regular fa-bell"></i>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">Notify Internal Teams</Typography>
                    <Typography variant="caption">Send onboarding alerts to internal teams.</Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    sx={{ position: 'relative', width: '2vw', height: '4vh' }}
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ mb: '1.2vh', mt: '2vh' }} />

            {/* Body */}
            <DialogContent sx={{ overflowY: 'auto', pt: '0.56vw' }}>
                <Box sx={{ mt: 1 }}>
                    <TextField
                        label="Email Address(es)"
                        size="small"
                        fullWidth
                        margin="dense"
                        placeholder="Type email and press Enter or Comma"
                        onKeyDown={handleEmailKeyDown}
                        error={!!errors.emails}
                        helperText={errors.emails}
                    />

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                        {emails.map((em) => {
                            const isValid = validateEmail(em); // Use your regex validation
                            return (
                                <Chip
                                    key={em}
                                    label={em}
                                    onDelete={() => handleDeleteEmail(em)}
                                    color={isValid ? "primary" : "error"} // Red for invalid
                                    variant="outlined"
                                    sx={{
                                        borderColor: isValid ? theme.palette.primary.main : theme.palette.error.main,
                                        backgroundColor: isValid ? "inherit" : "#ffe6e6",
                                    }}
                                />
                            );
                        })}
                    </Box>

                </Box>



                <FormControl fullWidth margin="dense" sx={{ mt: '2vh' }} required error={!!errors.team}>
                    <InputLabel size="small">Select team to notify</InputLabel>
                    <Select
                        size="small"
                        label="Select team to notify"
                        value={selectedTeam}
                        required
                        onChange={handleSelectTeamTemplate}
                    >
                        {filteredTemplates.map((option) => (
                            <MenuItem key={option.EmailNum} value={option.EmailKey}>
                                {option.EmailSubject}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.team && <FormHelperText>{errors.team}</FormHelperText>}
                </FormControl>

                <TextField
                    label="Subject"
                    size="small"
                    fullWidth
                    margin="dense"
                    required
                    value={emailSubject}
                    onChange={(e) => handleInputChange("subject", e.target.value)}
                    error={!!errors.subject}
                    helperText={errors.subject}
                />

                <Typography variant='caption' sx={{ mb: 0, mt: 1 }}>
                    Email Body<span style={{ color: theme.palette.error.main }}>* </span>
                </Typography>

                <ReactQuill
                    value={emailBody}
                    onChange={(val) => handleInputChange("body", val)}
                    style={{ height: '28vh', marginTop: '1vh', marginBottom: '2.5vw' }}
                    modules={{
                        toolbar: [['bold', 'italic', 'link', { list: 'ordered' }, { list: 'bullet' }]],
                    }}
                />
                {errors.body && <Typography variant="caption" color="error">{errors.body}</Typography>}



            </DialogContent>

            {/* Footer */}
            <Divider />
            <DialogActions sx={{ padding: '2vh' }}>
                <Button
                    variant="contained" fullWidth
                    onClick={handleSend}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default NotifyInternalTeam